import posthog from 'posthog-js';
import {
  opaqueDistinctId,
  personPropsFromUser,
  partnerTier,
  assertSafePersonProps,
} from './analyticsIdentity';
import { hasValidPostHogConfig, isEnergyProductionHost } from './posthogConfig';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const POSTHOG_ENABLED = import.meta.env.VITE_POSTHOG_ENABLED === 'true';

export const APP_NAME = 'eagle-vdr';
export let posthogEnabled = false;

const SUPER_PROPS = {
  app: APP_NAME,
  product: 'd2d-energy',
  deal_id: 'eagle-etx-2026',
};

/**
 * Init PostHog for the Eagle VDR (Vite SPA).
 * Production-only capture records product actions and session replays for every
 * user. Chat inputs remain covered by the server-side AI generation event.
 */
export function initPostHog() {
  if (posthogEnabled) return posthog;
  if (
    !POSTHOG_ENABLED ||
    typeof window === 'undefined' ||
    !isEnergyProductionHost(window.location.hostname) ||
    !hasValidPostHogConfig(POSTHOG_KEY, POSTHOG_HOST)
  ) {
    return posthog;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: '2026-01-30',
    capture_pageview: false,
    capture_pageleave: true,
    person_profiles: 'identified_only',
    autocapture: true,
    disable_session_recording: false,
    session_recording: {
      // Credentials and browser form fields remain masked; complete AI turns are
      // captured server-side in $ai_generation events.
      maskAllInputs: true,
    },
  });
  posthog.register(SUPER_PROPS);
  posthogEnabled = true;
  return posthog;
}

/** Hash-route → stable pathname for analytics (no token query strings). */
export function normalizePath(hashOrPath) {
  const raw = String(hashOrPath || '').replace(/^#/, '');
  if (!raw || raw === 'decision') return '/decision';
  if (raw.startsWith('reset')) return '/reset';
  if (raw.startsWith('admin')) return '/admin';
  if (raw === 'forgot') return '/forgot';
  if (raw.startsWith('well/')) return '/well';
  return `/${raw.split('?')[0]}`;
}

/**
 * Identify with opaque `energy-user-<id>` only.
 * Never uses or aliases email as the distinct ID. Authenticated display names
 * are set as person properties so PostHog can show people human-readably.
 */
export function identifyUser(user) {
  if (!posthogEnabled || !user) return;
  try {
    const opaque = opaqueDistinctId(user);
    if (!opaque) return;

    const props = personPropsFromUser(user);
    assertSafePersonProps(props);
    const tier = partnerTier(user);

    posthog.identify(opaque, props);

    posthog.register({
      ...SUPER_PROPS,
      role: user.role || undefined,
      partner_tier: tier || undefined,
      firm: props.firm || undefined,
    });

    // Session tags for recording and funnel segmentation.
    posthog.setPersonPropertiesForFlags?.(props);
    if (typeof posthog.setSessionProps === 'function') {
      posthog.setSessionProps({
        partner_tier: tier || undefined,
        firm: props.firm || undefined,
        role: user.role || undefined,
        session_tagged: true,
      });
    } else {
      posthog.capture('$set', {
        $set: props,
        session_tagged: true,
        partner_tier: tier || undefined,
      });
    }
  } catch {
    /* never break auth */
  }
}

export function resetUser() {
  if (!posthogEnabled) return;
  try {
    posthog.reset();
    posthog.register(SUPER_PROPS);
  } catch {
    /* ignore */
  }
}

/** Headers so the auth API can attribute server-side events to this browser session. */
export function posthogRequestHeaders() {
  if (!posthogEnabled) return {};
  try {
    const distinct = posthog.get_distinct_id?.();
    const session = posthog.get_session_id?.();
    const headers = {};
    if (distinct) headers['X-POSTHOG-DISTINCT-ID'] = distinct;
    if (session) headers['X-POSTHOG-SESSION-ID'] = session;
    return headers;
  } catch {
    return {};
  }
}

export { posthog };
